import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { getDemoSession } from '@/lib/demo';
import { useSubcontractorProfile } from '@/contexts/SubcontractorProfileContext';
import { fetchSceneDefs, unlockedPartsAt, partForRoute, ALL_PART_KEYS } from '@/lib/demoScenes';

// ── DemoAccessContext ─────────────────────────────────────────────────────────
// The scene lock's single source of truth on an attendee device: which parts
// the presentation has opened so far. Active ONLY for demo identities
// (profile.is_demo) holding a session code — for everyone else the hook says
// "everything open" and subscribes to nothing, so real crews are untouchable
// by construction.
//
// Realtime is the primary transport (its own channel topic, so DemoDeckBar's
// subscription is undisturbed); relaxed polling is the safety net. Every
// failure path fails OPEN: a phone that cannot read the session unlocks
// rather than bricking in front of a buyer. This is stagecraft, not security —
// RLS remains the real boundary and identities are purged at session end.
// Matches DemoDeckBar: phones drop the websocket on lock/background, so the
// fallback poll is what the room actually experiences — 4s, plus an instant
// catch-up on wake, keeps the scene lock in step with the deck.
const POLL_MS = 4000;

const OPEN_EVERYTHING = {
  active: false,
  isPartOpen: () => true,
  isRouteOpen: () => true,
  unlockedParts: new Set(ALL_PART_KEYS),
  showPricing: true,
};

const DemoAccessContext = createContext(OPEN_EVERYTHING);

export const useDemoAccess = () => useContext(DemoAccessContext);

export const DemoAccessProvider = ({ children }) => {
  const { profile } = useSubcontractorProfile();
  const [scenes, setScenes] = useState(null);
  const [session, setSession] = useState(null);
  const endedRef = useRef(false);

  const code = (() => {
    try { return localStorage.getItem('demo_session_code'); } catch { return null; }
  })();

  const isDemo = !!profile?.is_demo && !!code;

  useEffect(() => {
    if (!isDemo) return undefined;
    let stale = false;
    fetchSceneDefs().then((list) => { if (!stale) setScenes(list); }).catch(() => {});
    return () => { stale = true; };
  }, [isDemo]);

  useEffect(() => {
    if (!isDemo) return undefined;
    let stop = false;

    const applySession = (s) => {
      if (stop || endedRef.current) return;
      // Failed call: keep the last known lock state (never loaded → still
      // fails open). Only a server-confirmed answer changes anything.
      if (!s || s.status === 'error') return;
      if (s.status === 'not_found' || s.status === 'ended') {
        // Session over — the takeover screen owns the device; unlock so the
        // goodbye flow is never fenced in.
        endedRef.current = true;
        setSession(null);
        return;
      }
      // A payload without access_mode predates the scene-lock migration: the
      // presenter would have no override chips, so the lock must not engage.
      // Everything stays open until the schema carries the feature.
      if (s.access_mode === undefined || s.access_mode === null) {
        setSession(null);
        return;
      }
      setSession({
        current_scene: Math.max(0, s.current_scene ?? 0),
        access_mode: s.access_mode,
        opened_parts: Array.isArray(s.opened_parts) ? s.opened_parts : [],
        // Absent pre-migration → pricing shows, matching today's behaviour.
        show_pricing: s.show_pricing !== false,
      });
    };

    const tick = async () => {
      if (endedRef.current) return;
      try { applySession(await getDemoSession(code)); } catch { /* fail open */ }
    };

    // Own channel topic — never shares state with DemoDeckBar's subscription.
    const channel = supabase
      .channel(`demo-access-${code}`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'demo_sessions', filter: `code=eq.${code}` },
        (payload) => applySession(payload.new))
      .subscribe();

    tick();
    const id = setInterval(tick, POLL_MS);
    const wake = () => { if (document.visibilityState !== 'hidden') tick(); };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    return () => {
      stop = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('focus', wake);
      supabase.removeChannel(channel);
    };
  }, [isDemo, code]);

  const value = useMemo(() => {
    // Not a demo device, or anything still loading / gone wrong → everything
    // open. The lock only ever engages on a fully-informed demo device.
    if (!isDemo || !scenes || scenes.length === 0 || !session) return OPEN_EVERYTHING;
    const unlocked = unlockedPartsAt(
      scenes,
      Math.min(session.current_scene, scenes.length - 1),
      session.opened_parts,
      session.access_mode,
    );
    const isPartOpen = (pk) => !pk || unlocked.has(pk);
    return {
      active: true,
      isPartOpen,
      isRouteOpen: (pathname) => isPartOpen(partForRoute(pathname)),
      unlockedParts: unlocked,
      showPricing: session.show_pricing !== false,
    };
  }, [isDemo, scenes, session]);

  return <DemoAccessContext.Provider value={value}>{children}</DemoAccessContext.Provider>;
};

export default DemoAccessContext;
